// Loads the wgpu engine's wasm viewport (engine/, from `pnpm engine:pull`, served at /engine/). Where it
// can't run (a browser without WebGPU, or with WebGPU but no GPU it can use, or a server without the
// viewport), it says why instead, and the app draws the 2D preview (the mock) and tells the visitor.

import { onceMore } from '../data/retry.ts';
import type { Viewport, ViewportOptions } from './api.ts';

interface EngineModule {
  default: (input?: unknown) => Promise<unknown>;
  Viewport: { create(canvas: HTMLCanvasElement, options: ViewportOptions): Promise<Viewport> };
}

export const ENGINE_URL = '/engine/ba_viewport.js';

/** How a page starts the engine. `strict` is the Labs route's (docs/contracts/lab-manifest-v0.md, Engine): the fixed GET
 *  URLs only, one try each, no HEAD probe of the module and no retry address, and a failure final, worded by the frame
 *  (NEURO-LABS-001's cold-start policy admits exact GETs only). The atlas keeps the probing, retrying start. `importFn`
 *  is for tests. */
export interface StartOptions {
  strict?: boolean;
  importFn?: (url: string) => Promise<unknown>;
}

/** Whether the server has the viewport, by a HEAD of its module: asked again if the network fails it, and a network that
 *  fails it twice is the network's error, not "no viewport here". */
export async function engineInstalled(fetchFn: typeof fetch = (...a) => fetch(...a)): Promise<boolean> {
  const res = await onceMore(() => fetchFn(ENGINE_URL, { method: 'HEAD' }));
  return res.ok && (res.headers.get('content-type') ?? '').includes('javascript');
}

/** The engine's module, initialised. The browser keeps an import that failed as failed for the page's life (Chrome 154:
 *  the second import of the URL fails without a request), so the one more try asks for the module under another URL.
 *  The wasm's own fetch, which `default()` makes, is asked again as it is. */
export async function loadEngine(importFn: (url: string) => Promise<unknown> = (url) => import(/* @vite-ignore */ url), strict = false): Promise<EngineModule> {
  if (strict) {
    // One import of the fixed URL and one `default()` (the wasm's own fixed GET): no retry address, a failure final.
    const mod = (await importFn(ENGINE_URL)) as EngineModule;
    await mod.default();
    return mod;
  }
  const mod = (await onceMore((again) => importFn(again ? `${ENGINE_URL}?retry=1` : ENGINE_URL))) as EngineModule;
  await onceMore(() => mod.default());
  return mod;
}

/** Why a page has no wasm viewport: no WebGPU in the browser, WebGPU with no GPU it can use (hardware
 *  acceleration off, a blocked driver), no viewport on the server, or one that failed to start. */
export interface NoViewport {
  why: 'no-webgpu' | 'no-adapter' | 'no-engine' | 'failed';
  detail?: string;
}

/** Whether this browser can draw with WebGPU: it has `navigator.gpu`, and that finds an adapter. */
export async function gpuSupport(nav: { gpu?: { requestAdapter(): Promise<unknown> } } = navigator as never): Promise<'ok' | 'no-webgpu' | 'no-adapter'> {
  if (!nav.gpu) return 'no-webgpu';
  try {
    return (await nav.gpu.requestAdapter()) ? 'ok' : 'no-adapter';
  } catch {
    return 'no-adapter';
  }
}

export async function createWgpuViewport(canvas: HTMLCanvasElement, options: ViewportOptions, start: StartOptions = {}): Promise<Viewport | NoViewport> {
  const gpu = await gpuSupport();
  if (gpu !== 'ok') return { why: gpu };
  if (start.strict) {
    // No HEAD probe: the import itself says whether the server has the viewport, and any failure is `failed`, with no
    // detail, for the frame's fixed wording.
    try {
      const mod = await loadEngine(start.importFn, true);
      return await mod.Viewport.create(canvas, options);
    } catch {
      return { why: 'failed' };
    }
  }
  if (!(await engineInstalled())) return { why: 'no-engine' };
  const mod = await loadEngine(start.importFn);
  return mod.Viewport.create(canvas, options);
}
